import type { ReactNode } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import {
  WidgetChip,
  type WidgetChipState,
} from '#src/atoms/ui/Chip/WidgetChip/index.tsx';
import { OrbCircle, type OrbArcText } from '#src/atoms/ui/OrbCircle/index.tsx';
import { WidgetButton } from '#src/atoms/ui/WidgetButton/index.tsx';
import type { Unit } from '#src/lib/style.ts';

const StyledWidgetButton = styled(WidgetButton)`
  padding-top: var(--unit);
`;

const Positionned = styled.div`
  position: absolute;
  left: 50%;
  top: 55%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

type Props = {
  /** Drives the rim and, when glowing, the inner glow. */
  color: string;
  size?: Unit;
  label?: ReactNode;
  chipState?: WidgetChipState;
  arcText?: OrbArcText;
  disabled?: boolean;
  loading?: boolean;
  glow?: boolean;
  children?: ReactNode;
  showNotificationDot?: boolean;
  'aria-label'?: string;
} & ({ to: To; onClick?: () => void } | { to?: never; onClick?: () => void });

export const Orb = ({
  color,
  size,
  label,
  chipState,
  arcText,
  to,
  onClick,
  disabled,
  loading,
  glow = true,
  children,
  showNotificationDot = false,
  ...props
}: Props) => {
  const inactive = disabled || loading;

  return (
    <StyledWidgetButton
      {...props}
      {...(to && !inactive ? { to, onClick } : { onClick, disabled: inactive })}
      chip={
        label || chipState ? (
          <WidgetChip state={chipState}>{label}</WidgetChip>
        ) : undefined
      }
    >
      {!!loading && (
        <Positionned>
          <LoadingIndicator smaller />
        </Positionned>
      )}
      <Badge
        overlap="circular"
        badgeContent={!disabled && showNotificationDot ? 1 : undefined}
      >
        <OrbCircle
          size={size}
          rim={disabled ? 'var(--c-nd-300)' : color}
          glowColor={!disabled && glow ? color : undefined}
          arcText={arcText}
          loading={loading}
        >
          {children}
        </OrbCircle>
      </Badge>
    </StyledWidgetButton>
  );
};
